import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import DashboardLayout from "../views/DashboardLayout.vue";
import DashboardView from "../views/DashboardView.vue";

import { configureApiRouter } from "@/services/api";
import { useAuthStore } from "@/stores/AuthStore";
import { requireAdmin, requireAuth, requireGuest, requirePermission, requireRole } from "./guards";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/conferencia-web/:defesaId",
    name: "conferencia-web-convidado",
    component: () => import("../views/ConferenciaWebGuestView.vue"),
    meta: { title: "Entrar na Defesa" },
  },
  {
    path: "/",
    redirect: "/projetos",
  },
  {
    path: "/login",
    name: "login",
    component: () => import("../views/auth/LoginView.vue"),
    beforeEnter: requireGuest,
    meta: { title: "Login" },
  },
  {
    path: "/recuperar-senha",
    name: "forgot-password",
    component: () => import("../views/auth/ForgotPasswordView.vue"),
    beforeEnter: requireGuest,
    meta: { title: "Recuperar Senha" },
  },
  {
    path: "/redefinir-senha",
    name: "reset-password",
    component: () => import("../views/auth/ResetPasswordView.vue"),
    beforeEnter: requireGuest,
    meta: { title: "Redefinir Senha" },
  },
  {
    path: "/unauthorized",
    name: "unauthorized",
    component: () => import("../views/auth/UnauthorizedView.vue"),
    meta: { title: "Acesso Negado" },
  },

  {
    path: "/projetos",
    component: DashboardLayout,
    beforeEnter: requireAuth,
    children: [
      {
        path: "",
        name: "dashboard",
        component: DashboardView,
        meta: { title: "Dashboard" },
      },
      {
        path: "dashboard",
        redirect: "/projetos",
      },
      {
        path: "editor/:id",
        name: "editor",
        component: () => import("../views/EditorView.vue"),
        beforeEnter: requirePermission("trabalho.view"),
        props: (route) => ({ id: route.params.id }),
        meta: { title: "Editor" },
      },
    ],
  },

  {
    path: "/eventos",
    component: DashboardLayout,
    beforeEnter: requireAuth,
    children: [
      {
        path: "",
        name: "eventos",
        component: () => import("../views/eventos/EventosListView.vue"),
        meta: { title: "Eventos" },
      },
    ],
  },

  {
    path: "/logs",
    component: DashboardLayout,
    children: [
      {
        path: "",
        name: "logs",
        component: () => import("../views/logs/LogsView.vue"),
        beforeEnter: requirePermission("sistema.logs"),
        meta: {
          title: "Log do sistema",
        },
      },
    ],
  },

  {
    path: "/tutoriais",
    component: DashboardLayout,
    beforeEnter: requireAuth,
    children: [
      {
        path: "",
        name: "tutoriais",
        component: () => import("../views/TutoriaisView.vue"),
        meta: { title: "Tutoriais" },
      },
    ],
  },

  {
    path: "/normas-tcc",
    component: DashboardLayout,
    beforeEnter: requireRole("aluno"),
    children: [
      {
        path: "",
        name: "normas-tcc",
        component: () => import("../views/NormasTccView.vue"),
        meta: { title: "Normas TCC / Links" },
      },
    ],
  },

  {
    path: "/conta",
    component: DashboardLayout,
    beforeEnter: requireAuth,
    children: [
      {
        path: "",
        name: "conta",
        component: () => import("../views/ContaView.vue"),
        meta: { title: "Conta" },
      },
    ],
  },

  {
    path: "/acompanhamento",
    component: DashboardLayout,
    beforeEnter: requireRole("aluno"),
    children: [
      {
        path: "",
        name: "acompanhamento",
        component: () => import("../views/feedbackDefesa/FeedbackListView.vue"),
        meta: { title: "Acompanhamento de Trabalhos" },
      },
      {
        path: ":id",
        name: "acompanhamento-detalhes",
        component: () => import("../views/feedbackDefesa/FeedbackView.vue"),
        props: true,
        meta: { title: "Detalhes do Trabalho" },
      },
    ],
  },

  {
    path: "/admin",
    component: DashboardLayout,
    beforeEnter: requireAdmin,
    children: [
      {
        path: "",
        name: "admin-dashboard",
        component: () => import("../views/admin/AdminDashboard.vue"),
        meta: { title: "Administração" },
      },
      {
        path: "templates",
        name: "admin-templates",
        component: () => import("../views/admin/TemplateManager.vue"),
        beforeEnter: requirePermission("template.manage"),
        meta: {
          title: "Templates",
        },
      },
      {
        path: "roles",
        name: "admin-roles",
        component: () => import("../views/admin/RoleManager.vue"),
        beforeEnter: requirePermission("role.manage"),
        meta: {
          title: "Perfis",
        },
      },
      {
        path: "permissions",
        name: "admin-permissions",
        component: () => import("../views/admin/PermissionManager.vue"),
        beforeEnter: requirePermission("permission.manage"),
        meta: {
          title: "Permissões",
        },
      },
      {
        path: "users",
        name: "admin-users",
        component: () => import("../views/admin/UserManager.vue"),
        beforeEnter: requirePermission("user.manage"),
        meta: {
          title: "Usuários",
        },
      },
      {
        path: "cursos",
        name: "admin-cursos",
        component: () => import("../views/admin/CursosView.vue"),
        meta: {
          title: "Cursos",
        },
      },
      {
        path: "trabalhos",
        name: "admin-trabalhos",
        component: () => import("../views/admin/TrabalhosAdminView.vue"),
        meta: {
          title: "Gerenciar Trabalhos",
        },
      },
      {
        path: "coordenadores",
        name: "admin-coordenadores",
        component: () => import("../views/admin/CoordenadoresView.vue"),
        meta: {
          title: "Coordenadores",
        },
      },
      {
        path: "coordenadores-tcc",
        name: "admin-coordenadores-tcc",
        component: () => import("../views/admin/CoordenadoresTccView.vue"),
        meta: {
          title: "Coordenadores de TCC",
        },
      },
      {
        path: "secretarias",
        name: "admin-secretarias",
        component: () => import("../views/admin/SecretariasView.vue"),
        meta: {
          title: "Secretárias",
        },
      },
      {
        path: "solicitacoes-acesso",
        name: "admin-solicitacoes-acesso",
        component: () => import("../views/admin/SolicitacoesAcessoView.vue"),
        beforeEnter: requirePermission("user.manage"),
        meta: {
          title: "Solicitações de Acesso",
        },
      },
      {
        path: "professores",
        name: "admin-professores",
        component: () => import("../views/admin/ProfessoresView.vue"),
        meta: {
          title: "Professores",
        },
      },
      {
        path: "alunos",
        name: "admin-alunos",
        component: () => import("../views/admin/AlunosView.vue"),
        meta: {
          title: "Alunos",
        },
      },
      {
        path: "eduplay",
        name: "admin-eduplay",
        component: () => import("../views/admin/AdminEduplayView.vue"),
        meta: {
          title: "Integração Eduplay",
        },
      },
      {
        path: "normas-tcc",
        name: "admin-normas-tcc",
        component: () => import("../views/admin/NormasTccAdminView.vue"),
        meta: {
          title: "Normas TCC / Links",
        },
      },
    ],
  },

  {
    path: "/gerenciar-trabalhos",
    component: DashboardLayout,
    beforeEnter: requireRole(["coordenador", "secretaria", "coordenador_tcc"]),
    children: [
      {
        path: "",
        name: "coordenador-trabalhos",
        component: () => import("../views/coordenador/CoordenadorTrabalhosView.vue"),
        meta: {
          title: "Gerenciar trabalhos",
        },
      },
      {
        path: ":id",
        name: "coordenador-trabalho-detalhes",
        component: () => import("../views/coordenador/CoordenadorAcompanhamentoDetalheView.vue"),
        meta: {
          title: "Detalhes do Trabalho",
        },
      },
    ],
  },
  {
    path: "/avisos",
    component: DashboardLayout,
    beforeEnter: requireRole(["coordenador", "secretaria", "coordenador_tcc"]),
    children: [
      {
        path: "",
        name: "coordenador-avisos",
        component: () => import("../views/coordenador/CoordenadorAvisosView.vue"),
        meta: {
          title: "Central de Avisos",
        },
      },
    ],
  },
  { path: "/coordenador/acompanhamento", redirect: "/gerenciar-trabalhos" },
  { path: "/coordenador/acompanhamento/:id", redirect: (to) => `/gerenciar-trabalhos/${to.params.id}` },
  {
    path: "/gerenciar-professores",
    component: DashboardLayout,
    beforeEnter: requireRole(["coordenador", "secretaria"]),
    children: [
      {
        path: "",
        name: "coordenador-professores-curso",
        component: () => import("../views/coordenador/CoordenadorProfessoresView.vue"),
        meta: {
          title: "Gerenciar Professores",
          requiresRole: ["coordenador", "secretaria"],
        },
      },
    ],
  },

  {
    path: "/gerenciar-normas-tcc",
    component: DashboardLayout,
    beforeEnter: requireRole(["coordenador", "secretaria", "coordenador_tcc"]),
    children: [
      {
        path: "",
        name: "gerenciar-normas-tcc",
        component: () => import("../views/coordenador/NormasTccGestaoView.vue"),
        meta: {
          title: "Gerenciar Normas TCC",
          requiresRole: ["coordenador", "secretaria", "coordenador_tcc"],
        },
      },
    ],
  },

  {
    path: "/painel-orientador",
    component: DashboardLayout,
    beforeEnter: requireRole("orientador"),
    children: [
      {
        path: "",
        name: "orientador-dashboard",
        component: () => import("../views/orientador/OrientadorDashboard.vue"),
        meta: {
          title: "Painel do Orientador",
        },
      },
      {
        path: "editor/:id",
        name: "orientador-editor",
        component: () => import("../views/EditorView.vue"),
        props: (route) => ({ id: route.params.id }),
        meta: { title: "Editor" },
      },
    ],
  },

  {
    path: "/defesas",
    component: DashboardLayout,
    beforeEnter: requireRole("orientador"),
    children: [
      {
        path: "",
        name: "orientador-defesas",
        component: () => import("../views/orientador/OrientadorDefesasView.vue"),
        meta: {
          title: "Revisão de Defesas",
        },
      },
    ],
  },

  {
    path: "/:pathMatch(.*)*",
    name: "notfound",
    component: () => import("../views/NotFoundView.vue"),
    meta: { title: "Página Não Encontrada" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

configureApiRouter(router);

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();

  if (authStore.initialized && authStore.isAuthenticated && authStore.user?.must_change_password && to.name !== 'conta') {
    return next({ name: 'conta', query: { security: '1' } });
  }

  const appName = "TCC";
  const title = to.meta?.title;
  document.title = title ? `${title} | ${appName}` : appName;
  next();
});

export default router;
